import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Form } from 'react-redux-form';
import { duckify, singleDuck, withForm } from 'utils/duckify';
import FontAwesome from 'utils/FA';
import grades from '../../../../config/shared_portal_grades.json';
import { sharedPortalConfigForm, grades as gradesDuck } from 'ducks';
import { sharedPortalConfigs as sharedPortalDuck } from '../../ducks/sharedPortalConfigs';
import Placeholder from '../Placeholder';
import {Creatable} from "react-select";
import {apiFetch} from "../../utils";
import './styles/SharedPortalConfig.scss';

const SharedPortalConfig = ({
  sharedPortalConfig,
  save,
  systemGrades
}) => (
  <Placeholder.loading ready={!!sharedPortalConfig.id && systemGrades.ready}>
    <SharedPortalConfigForm sharedPortalConfig={sharedPortalConfig} save={save} systemGrades={systemGrades} />
  </Placeholder.loading>
);

const RequiredAsterisk = () => <span style={{ color: 'red' }}>*</span>;

const SharedPortalConfigForm = ({
  sharedPortalConfig,
  save,
  systemGrades
}) => {
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(sharedPortalConfig);
  const [logoFile, setLogoFile] = useState(null);
  const [programOptions, setProgramOptions] = useState(null);

  const firstGrade = grades.findIndex((grade) => grade === config.min_grade) || 0;
  const lastGrade = grades.findIndex((grade) => grade === config.max_grade) || (grades.length - 1);
  const filteredGrades = grades.slice(firstGrade, lastGrade + 1);

  useEffect(() => {
    if (sharedPortalConfig.shared_portal_groups_ids) {
      const params = new URLSearchParams();
      sharedPortalConfig.shared_portal_groups_ids.forEach(id => {
        params.append('shared_portal_groups_ids[]', id);
      });

      apiFetch.get(
        {},
        `shared_portal_groups/programs?${params.toString()}`
      )
        .then(json => setProgramOptions(json.programs.map(program => ({ label: program, value: program }))))
        .catch(() => setProgramOptions([]));
    } else {
      setProgramOptions([]);
    }
  }, []);

  const updateConfig = (e) => {
    const modifiedConfig = { ...config };
    modifiedConfig[e.currentTarget.name] = e.currentTarget.value;
    setConfig(modifiedConfig);
  };

  const updateGradeMap = (e) => {
    const modifiedConfig = { ...config };
    modifiedConfig.grade_map[e.currentTarget.name] = e.currentTarget.value;
    setConfig(modifiedConfig);
  };

  const updateLogo = (e) => {
    setLogoFile(e.target.files[0]);
  };

  const topRef = useRef();
  const onSubmit = ({ config, logoFile }) => {
    setSaving(true);
    save({ config, logoFile }, setSaving);
  };

  const toggleEnabled = () => {
    const modifiedConfig = { ...config };
    modifiedConfig.enabled = !config.enabled;
    setConfig(modifiedConfig);
  };

  return (
    <Form model='sharedPortalConfigForm.model' className='form-horizontal' name='shared_portal_config'>
      <h2 className='shared-portal-header' ref={topRef}>Finalsite Explore Content</h2>
      <legend>School Information</legend>
      <div className='base-setup-fields'>
        <div className='row base-setup-field-group'>
          <label className="base-setup-field-label">
            <b>
              School Name
              <RequiredAsterisk />
            </b>
          </label>
          <div className="col-12 base-setup-field-value">
            <input
              disabled={saving}
              className='base-setup-field-input'
              name='school_name'
              type='text'
              value={config.school_name}
              onChange={updateConfig}
            />
          </div>
        </div>

        <div className='row base-setup-field-group'>
          <label className="base-setup-field-label">
            <b>
              School Information Snippet
              <RequiredAsterisk />
            </b>
          </label>
          <div className="col-12 base-setup-field-value">
            <textarea
              disabled={saving}
              className='base-setup-field-input'
              name='description'
              type='text'
              value={config.description}
              onChange={updateConfig}
              maxLength={500}
            />
          </div>
          <label className="form-text">
            Maximum 500 characters
          </label>
        </div>

        <div className='row base-setup-field-group'>
          <div className="col-6 base-setup-field-value">
            <label className="base-setup-field-label">
              <b>
                Upload School Logo
                <RequiredAsterisk />
              </b>
            </label>
            <input name='shared_portal_logo' className='form-control form-control-sm base-setup-field-file' type="file" onChange={(e) => updateLogo(e)} />
          </div>
          <div className='sp-dashboard-logo-column'>
            {sharedPortalConfig.shared_portal_logo_url && <img src={sharedPortalConfig.shared_portal_logo_url} alt='School Logo' className='sp-dashboard-school-logo' />}
          </div>
        </div>

        <div className='row base-setup-field-group'>
          <label className="base-setup-field-label">
            <b>
              More Information Hyperlink
              <RequiredAsterisk />
            </b>
          </label>
          <div className="col-12 base-setup-field-value">
            <input
              disabled={saving}
              className='base-setup-field-input'
              name='info_url'
              type='text'
              value={config.info_url}
              onChange={updateConfig}
            />
          </div>
          <label className="form-text">
            Example: https://www.schoolame.edu/admission
          </label>
        </div>

        <div className='row base-setup-field-group'>
          <label className="base-setup-field-label">
            <b>
              Min Grade Level
              <RequiredAsterisk />
            </b>
          </label>
          <div className="col-12 base-setup-field-value">
            <select
              disabled={saving}
              value={config.min_grade}
              name={`min_grade`}
              onChange={updateConfig}
              className='form-select form-select-sm'
            >
              {grades.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
        </div>

        <div className='row base-setup-field-group'>
          <label className="base-setup-field-label">
            <b>
              Max Grade Level
              <RequiredAsterisk />
            </b>
          </label>
          <div className="col-12 base-setup-field-value">
            <select
              disabled={saving}
              value={config.max_grade}
              name={`max_grade`}
              onChange={updateConfig}
              className='form-select form-select-sm'
            >
              {grades.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
        </div>

        <table className='col-12 sp-config-grades-table'>
          <thead>
            <tr>
              <th className='sp-config-portal-grade'>Portal Grade</th>
              <th className='sp-config-system-grade'>
                System Grade
                <OverlayTrigger overlay={<Tooltip>Inquiries from Finalsite Explore will feed into the grade selected.</Tooltip>}>
                  <FontAwesome name='info-circle' preNbsp />
                </OverlayTrigger>
                <RequiredAsterisk />
              </th>
            </tr>
          </thead>
          <tbody>
            {(filteredGrades.length < grades.length ? ['default'] : []).concat(filteredGrades).map(grade => (
              <tr key={grade}>
                <td className='sp-grade-map-portal-label'>{grade === 'default' ? 'Outside of Range' : grade}</td>
                <td>
                  <select
                    disabled={saving}
                    value={config.grade_map[grade]}
                    name={grade}
                    onChange={updateGradeMap}
                    className='form-select form-select-sm'
                  >
                    {[{}].concat(systemGrades).map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className='row base-setup-field-group'>
          <label className="base-setup-field-label">
            <b>
              School Address (Shown in a single line)
              <RequiredAsterisk />
            </b>
          </label>
          <div className="col-12 base-setup-field-value">
            <input
              disabled={saving}
              className='base-setup-field-input'
              name='address'
              type='text'
              value={config.address}
              onChange={updateConfig}
            />
          </div>
          <label className="form-text">
            Example: 1010 Martin Ave. Atlanta, GA 77777
          </label>
        </div>

        <div className='row base-setup-field-group sp-config-programs'>
          <label className="base-setup-field-label">
            <b>
              Programs
            </b>
          </label>

          <div className="col-12 base-setup-field-value">
            <Creatable
              multi
              onChange={selected => setConfig({ ...config, programs: selected.map(program => program.value) })}
              options={programOptions}
              value={config.programs}
              disabled={saving || !programOptions}
            />
          </div>
        </div>

        <div className='row base-setup-field-group'>
          <div className="col-12 base-setup-field-checkbox-value">
            <input
              disabled={saving}
              className='base-setup-field-checkbox'
              name='enabled'
              type='checkbox'
              checked={!!config.enabled}
              onChange={toggleEnabled}
            />
          </div>
          <label className="base-setup-field-checkbox-label">
            <b>Enabled</b>
          </label>
        </div>

        <Button
          size='md'
          className='col-3 btn btn-primary'
          type='submit'
          onClick={() => onSubmit({ config, logoFile })}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </Form>
  );
}

SharedPortalConfig.propTypes = {
  sharedPortalConfig: PropTypes.shape({
    school_name: PropTypes.string,
    enabled: PropTypes.bool,
    min_grade: PropTypes.string,
    max_grade: PropTypes.string,
    description: PropTypes.string,
    info_url: PropTypes.string,
    address: PropTypes.string,
    id: PropTypes.string,
    errors: PropTypes.shape({})
  }),
  save: PropTypes.func,
  systemGrades: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })).isRequired
};

SharedPortalConfig.defaultProps = {
  sharedPortalConfig: {
    school_name: '',
    enabled: false,
    min_grade: 'Nursery',
    max_grade: 'Post Grad',
    description: '',
    info_url: '',
    address: '',
    id: 'current',
    errors: {}
  },
  save: () => false
};

const mapDuckToProps = () => {
  const sharedPortalConfig = singleDuck(sharedPortalDuck, 'current');

  return {
    sharedPortalConfig,
    systemGrades: gradesDuck,
    sharedPortalConfigForm: withForm(sharedPortalConfig, sharedPortalConfigForm)
  };
};

// noinspection JSUnusedGlobalSymbols
const mapDispatchToProps = (dispatch) => ({
  save: (attr, setSaving) => {
    const handleSave = (m, f) => dispatch(sharedPortalDuck.actions.update(m, f))
      .then(() => Utility.displayFlashMessages({ success: 'Finalsite Explore Content saved.' }))
      .then(() => dispatch(sharedPortalDuck.actions.markDirty()))
      .catch(response => {
        if (response.shared_portal_config.errors) {
          Utility.errorMessage(response.shared_portal_config.errors);
        }
        if (!response.shared_portal_config) {
          Utility.displayFlashMessages({ error: 'Sorry, something went wrong.' });
        }
      });

    Utility.hideNotifications();

    handleSave(attr.config, attr.logoFile).then(() => {
      setSaving(false);
    }, () => {
      Utility.displayFlashMessages({ error: 'Finalsite Explore Content not saved. You must confirm all changes.' });
    });
  }
});

export default duckify(mapDuckToProps, () => ({}), mapDispatchToProps)(SharedPortalConfig);
